import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Minimize2, Maximize2, GripVertical } from 'lucide-react';

interface DraggablePanelProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Initial width in px (default: 320) */
  width?: number;
  /** Initial height in px – if 0 or undefined, auto-fit content */
  height?: number;
  /** Min width (default: 240) */
  minWidth?: number;
  /** Min height (default: 150) */
  minHeight?: number;
  /** Enable resize handles (default: true) */
  resizable?: boolean;
  /** Initial position (defaults to top-right) */
  initialX?: number;
  initialY?: number;
  /** Extra header buttons */
  headerExtra?: React.ReactNode;
  /** Icon to show in header */
  icon?: React.ReactNode;
}

export const DraggablePanel: React.FC<DraggablePanelProps> = ({
  title,
  onClose,
  children,
  width: initW = 320,
  height: initH,
  minWidth = 240,
  minHeight = 150,
  resizable = true,
  initialX,
  initialY,
  headerExtra,
  icon,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: initialX ?? window.innerWidth - initW - 12, y: initialY ?? 12 });
  const [size, setSize] = useState({ w: initW, h: initH || 0 });
  const [minimized, setMinimized] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  /* ── Dragging ── */
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setDragging(true);
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  }, [pos]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOffset.current.y)),
      });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [dragging]);

  /* ── Resizing ── */
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    const rect = panelRef.current?.getBoundingClientRect();
    resizeStart.current = { x: e.clientX, y: e.clientY, w: rect?.width || size.w, h: rect?.height || size.h };
  }, [size]);

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (e: MouseEvent) => {
      const dx = e.clientX - resizeStart.current.x;
      const dy = e.clientY - resizeStart.current.y;
      setSize({
        w: Math.max(minWidth, resizeStart.current.w + dx),
        h: Math.max(minHeight, resizeStart.current.h + dy),
      });
    };
    const handleUp = () => setResizing(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [resizing, minWidth, minHeight]);

  const autoHeight = !initH || initH === 0;

  return (
    <div
      ref={panelRef}
      className="fixed z-50 flex flex-col overflow-hidden rounded-lg shadow-2xl"
      style={{
        left: pos.x,
        top: pos.y,
        width: size.w,
        ...(minimized ? {} : autoHeight && size.h === 0 ? { maxHeight: '85vh' } : { height: size.h || undefined, maxHeight: '85vh' }),
        background: 'var(--gn-glass-bg)',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--gn-glass-border)',
        userSelect: dragging || resizing ? 'none' : undefined,
      }}
    >
      {/* Header — drag handle */}
      <div
        onMouseDown={handleDragStart}
        className="flex items-center justify-between px-3 py-2 border-b border-gn shrink-0"
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical size={12} className="text-gn-secondary shrink-0" />
          {icon && <span className="text-gn-blue shrink-0">{icon}</span>}
          <span className="text-xs font-semibold text-gn-primary truncate">{title}</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {headerExtra}
          <button
            onClick={() => setMinimized(!minimized)}
            className="p-1 rounded hover:bg-gn-tertiary text-gn-secondary hover:text-gn-primary transition-colors"
          >
            {minimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gn-danger-light text-gn-secondary hover:text-gn-danger transition-colors"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Content */}
      {!minimized && (
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      )}

      {/* Resize handle */}
      {resizable && !minimized && (
        <div
          onMouseDown={handleResizeStart}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          style={{ zIndex: 10 }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" className="absolute bottom-1 right-1 text-gn-secondary">
            <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  );
};
